import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { CheckCircle2, Link2, Loader2, Mic2, Music, Plus, Search, X } from 'lucide-react';
import { toast } from 'sonner';
import { StageBackground } from '../../components/karaoke/StageBackground';
import { STATUS_STYLE, errMsg, parseYouTubeId, pub } from '../../lib/karaokeApi';

const emptySong = () => ({ url: '', title: '', thumbnail: '' });
const decode = (s) => { const t = document.createElement('textarea'); t.innerHTML = s || ''; return t.value; };
const tokKey = (code) => `ic_karaoke_tokens_${code}`;

const SongSearch = ({ code, onPick }) => {
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(false);
  const [items, setItems] = useState(null);
  const [more, setMore] = useState(false);
  const [notice, setNotice] = useState(null);
  const go = async (wantMore = false) => {
    if (q.trim().length < 2) return;
    setBusy(true);
    try {
      const r = await pub.search(code, q.trim(), wantMore);
      setItems(r.items); setMore(!!r.can_search_more); setNotice(r.notice || null);
    } catch (e) { toast.error(errMsg(e)); if (!wantMore) setItems([]); setMore(false); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), go())}
          placeholder="Song or artist" className="flex-1 min-w-0 rounded-xl bg-white/10 border border-white/20 px-3 h-11 text-white placeholder:text-white/50" data-testid="karaoke-search-input" />
        <button type="button" onClick={() => go()} disabled={busy} className="h-11 px-4 rounded-xl bg-fuchsia-500 text-white font-semibold" data-testid="karaoke-search-btn">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
        </button>
      </div>
      {items && (items.length ? (
        <div className="max-h-64 overflow-y-auto space-y-1.5" data-testid="karaoke-search-results">
          {items.map((it) => (
            <button key={it.video_id} type="button" onClick={() => { onPick(it); setItems(null); }} className="w-full flex items-center gap-3 p-2 rounded-xl bg-white/5 hover:bg-white/15 text-left">
              {it.thumbnail && <img src={it.thumbnail} alt="" className="w-20 h-12 object-cover rounded-lg shrink-0" />}
              <span className="min-w-0"><span className="block text-sm text-white font-semibold line-clamp-2">{decode(it.title)}</span><span className="block text-xs text-white/60">{it.channel}</span></span>
            </button>
          ))}
        </div>
      ) : <p className="text-sm text-white/60">No results — try another search or paste a link.</p>)}
      {items && more && (
        <button type="button" onClick={() => go(true)} disabled={busy} className="text-xs text-white/70 underline underline-offset-2" data-testid="karaoke-search-more">Search YouTube for more versions</button>
      )}
      {notice && <p className="text-xs text-amber-200">{notice}</p>}
    </div>
  );
};

const SongSlot = ({ n, song, onChange, onRemove, code, searchEnabled }) => {
  const [mode, setMode] = useState(searchEnabled ? 'search' : 'link');
  const vid = parseYouTubeId(song.url);
  return (
    <div className="rounded-2xl bg-black/40 border border-white/15 p-4 space-y-3 backdrop-blur" data-testid={`karaoke-song-slot-${n}`}>
      <div className="flex items-center justify-between">
        <p className="font-bold text-white">Song {n}</p>
        {onRemove && <button type="button" onClick={onRemove} className="text-white/70 p-1" aria-label={`Remove song ${n}`}><X className="w-5 h-5" /></button>}
      </div>
      {vid ? (
        <div className="flex items-center gap-3">
          <img src={song.thumbnail || `https://i.ytimg.com/vi/${vid}/mqdefault.jpg`} alt="" className="w-24 h-14 object-cover rounded-lg shrink-0" />
          <div className="min-w-0 flex-1">
            <input value={song.title} onChange={(e) => onChange({ ...song, title: e.target.value })} placeholder="Song title (optional)"
              className="w-full rounded-lg bg-white/10 border border-white/20 px-2 h-9 text-sm text-white placeholder:text-white/50" data-testid={`karaoke-song-title-${n}`} />
            <button type="button" onClick={() => onChange(emptySong())} className="mt-1 text-xs text-fuchsia-200 underline">Change song</button>
          </div>
        </div>
      ) : (
        <>
          {searchEnabled && (
            <div className="flex gap-2 text-xs">
              {[['search', 'Search', Search], ['link', 'Paste link', Link2]].map(([m, l, I]) => (
                <button key={m} type="button" onClick={() => setMode(m)} className={`flex items-center gap-1 px-3 py-1.5 rounded-full ${mode === m ? 'bg-white text-black' : 'bg-white/10 text-white'}`}><I className="w-3.5 h-3.5" /> {l}</button>
              ))}
            </div>
          )}
          {mode === 'search' && searchEnabled
            ? <SongSearch code={code} onPick={(it) => onChange({ url: it.video_id, title: decode(it.title), thumbnail: it.thumbnail })} />
            : (
              <div>
                <input value={song.url} onChange={(e) => onChange({ ...song, url: e.target.value })} placeholder="Paste YouTube link"
                  className="w-full rounded-xl bg-white/10 border border-white/20 px-3 h-11 text-white placeholder:text-white/50" data-testid={`karaoke-song-url-${n}`} />
                {song.url && !vid && <p className="text-xs text-rose-300 mt-1">That doesn't look like a YouTube video link.</p>}
              </div>
            )}
        </>
      )}
    </div>
  );
};

const MyRequests = ({ code, tokens }) => {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    if (!tokens.length) return undefined;
    const load = () => pub.mine(code, tokens).then(setRows).catch(() => {});
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [code, tokens]);
  if (!rows.length) return null;
  return (
    <div className="space-y-3" data-testid="karaoke-my-requests">
      <h2 className="text-white font-bold text-lg">My requests</h2>
      {rows.map((r) => (
        <div key={r.token} className="rounded-2xl bg-black/40 border border-white/15 p-3 space-y-2 backdrop-blur">
          {r.songs.map((s) => {
            const [label, cls] = STATUS_STYLE[s.status] || STATUS_STYLE.waiting;
            return (
              <div key={s.idx} className="flex items-center gap-3">
                <img src={s.thumbnail} alt="" className="w-14 h-9 object-cover rounded-md shrink-0" />
                <p className="flex-1 min-w-0 text-sm text-white break-words">{s.title || `Song ${s.idx}`}</p>
                <span className={`text-xs font-bold px-2.5 py-1 rounded-full whitespace-nowrap ${cls}`} data-testid={`karaoke-my-status-${s.idx}`}>{label}</span>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
};

export default function KaraokeRequest() {
  const { code } = useParams();
  const [info, setInfo] = useState(null);
  const [name, setName] = useState('');
  const [room, setRoom] = useState('');
  const [songs, setSongs] = useState([emptySong()]);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [tokens, setTokens] = useState(() => JSON.parse(localStorage.getItem(tokKey(code)) || '[]'));
  const nonce = useMemo(() => `${Date.now()}-${Math.random().toString(36).slice(2)}`, [done]); // eslint-disable-line react-hooks/exhaustive-deps

  const load = useCallback(() => pub.info(code).then(setInfo).catch(() => setInfo({ invalid: true })), [code]);
  useEffect(() => { load(); const t = setInterval(load, 20000); return () => clearInterval(t); }, [load]);

  const valid = name.trim() && room.trim() && songs.length && songs.every((s) => parseYouTubeId(s.url));
  const submit = async () => {
    setBusy(true);
    try {
      const r = await pub.submit(code, { guest_name: name.trim(), room_number: room.trim(), client_nonce: nonce,
        songs: songs.map((s) => ({ url: s.url, title: s.title || null, thumbnail: s.thumbnail || null })) });
      const next = [r.token, ...tokens.filter((t) => t !== r.token)].slice(0, 20);
      localStorage.setItem(tokKey(code), JSON.stringify(next));
      setTokens(next);
      setDone(true);
      setSongs([emptySong()]);
    } catch (e) { toast.error(errMsg(e, 'Could not send your request')); } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen relative" style={{ '--kglow': '#d946ef' }}>
      <div className="fixed inset-0"><StageBackground theme="purple_stage" /></div>
      <div className="relative z-10 max-w-lg mx-auto px-4 py-8 space-y-6">
        <header className="text-center text-white">
          <p className="tracking-[0.35em] text-xs text-white/70">IC.FUNFLOW PRESENTS</p>
          <h1 className="ktitle text-5xl font-black mt-2" style={{ fontFamily: '"Bebas Neue", "Oswald", Impact, sans-serif' }}>KARAOKE NIGHT</h1>
          {info?.hotel_name && <p className="text-sm text-white/70 mt-1">{info.hotel_name}</p>}
        </header>

        {!info && <Loader2 className="w-8 h-8 animate-spin text-white mx-auto" />}
        {info?.invalid && <p className="text-center text-white bg-black/40 rounded-2xl p-6">This Karaoke code is not valid.</p>}
        {info && !info.invalid && !info.active && (
          <div className="text-center text-white bg-black/50 rounded-2xl p-8 backdrop-blur" data-testid="karaoke-not-active">
            <Mic2 className="w-12 h-12 mx-auto mb-3 text-fuchsia-300" />
            <p className="text-xl font-bold">Karaoke is not currently active</p>
            <p className="text-white/70 mt-2 text-sm">Come back during Karaoke Night to request your songs.</p>
          </div>
        )}

        {info?.active && done && (
          <div className="text-center text-white bg-emerald-600/80 rounded-2xl p-6 backdrop-blur" data-testid="karaoke-request-confirmed">
            <CheckCircle2 className="w-12 h-12 mx-auto mb-2" />
            <p className="text-xl font-bold">Request sent!</p>
            <p className="text-sm text-white/90 mt-1">The DJ will add your songs to the playlist. Follow them below.</p>
            <button type="button" onClick={() => setDone(false)} className="mt-4 h-11 px-5 rounded-xl bg-white text-emerald-800 font-semibold" data-testid="karaoke-request-another">Request more songs</button>
          </div>
        )}

        {info?.active && !done && (
          <div className="space-y-4" data-testid="karaoke-request-form">
            <div className="grid grid-cols-3 gap-3">
              <input value={name} onChange={(e) => setName(e.target.value)} maxLength={60} placeholder="Your name" className="col-span-2 rounded-xl bg-white/10 border border-white/20 px-3 h-12 text-white placeholder:text-white/50" data-testid="karaoke-guest-name" />
              <input value={room} onChange={(e) => setRoom(e.target.value)} maxLength={12} placeholder="Room" className="rounded-xl bg-white/10 border border-white/20 px-3 h-12 text-white placeholder:text-white/50" data-testid="karaoke-guest-room" />
            </div>
            {songs.map((s, i) => (
              <SongSlot key={i} n={i + 1} song={s} code={code} searchEnabled={info.search_enabled}
                onChange={(v) => setSongs(songs.map((x, j) => (j === i ? v : x)))}
                onRemove={songs.length > 1 ? () => setSongs(songs.filter((_, j) => j !== i)) : null} />
            ))}
            {songs.length < 3 && (
              <button type="button" onClick={() => setSongs([...songs, emptySong()])} className="w-full h-12 rounded-xl border border-dashed border-white/40 text-white flex items-center justify-center gap-2" data-testid="karaoke-add-song">
                <Plus className="w-4 h-4" /> Add another song ({songs.length}/3)
              </button>
            )}
            <button type="button" onClick={submit} disabled={!valid || busy} className="w-full h-14 rounded-2xl bg-fuchsia-500 disabled:opacity-40 text-white text-lg font-bold flex items-center justify-center gap-2" data-testid="karaoke-submit">
              {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Music className="w-5 h-5" />} Send my request
            </button>
          </div>
        )}
        <MyRequests code={code} tokens={tokens} />
      </div>
    </div>
  );
}
